import { METHODS } from "@app/kit";
import {
  MerchantEmployeeConfigDTO,
  MerchantEmployeeDocumentsResponseDTO,
  MerchantEmployeeShiftStatsDTO,
  MerchantEmployeeShiftStatusDTO,
  type MerchantEmployeeDocumentDTO,
  type MerchantEmployeeStateDTO,
} from "@app/dto";

import { api } from "../api";
import { MOCK, mock } from "../mock";
import type { OutcomeNode, Questionnaire, ResultBody } from "./tree";

// Состояния сотрудника, их исходы и причины отказа ведутся в 1С мерчанта; сервер отдаёт справочник как есть.

export type StaffState = Omit<MerchantEmployeeStateDTO, "outcomes"> & { outcomes: OutcomeNode[] };
// questionnaires — анкеты «что спрашивал клиент»; askCustomerOnEnter / askFittingOnEnter — настройки мерчанта
// из 1С (необязательные): спрашивать клиента и примерку сразу при входе в консультацию.
export type StaffConfig = {
  states: StaffState[];
  questionnaires?: Questionnaire[];
  askCustomerOnEnter?: boolean;
  askFittingOnEnter?: boolean;
};
export type CandidateDocument = MerchantEmployeeDocumentDTO;
export type ShiftStatus = MerchantEmployeeShiftStatusDTO;
export type ShiftStats = MerchantEmployeeShiftStatsDTO;

// Все результаты консультации одним запросом; состояния без исходов завершаются пустым списком.
export interface FinishStateBody {
  results: ResultBody[];
}

const url = (path: string) => `/v1/merchant-employee/${path}`;

const configRequest = api.createRequest({
  url: url("config"),
  method: METHODS.GET,
  entity: MerchantEmployeeConfigDTO,
});
export const getConfig = async () => (MOCK ? mock.getConfig() : ((await configRequest()) as unknown as StaffConfig));

const shiftRequest = api.createRequest({
  url: url("shift"),
  method: METHODS.GET,
  entity: MerchantEmployeeShiftStatusDTO,
});
export const getShift = () => (MOCK ? mock.getShift() : shiftRequest());

const openShiftRequest = api.createRequest({
  url: url("shift/open"),
  method: METHODS.POST,
  entity: MerchantEmployeeShiftStatusDTO,
});
export const openShift = () => (MOCK ? mock.openShift() : openShiftRequest());

const enterStateRequest = api.createRequest({
  url: url("shift/state/enter"),
  method: METHODS.POST,
  entity: MerchantEmployeeShiftStatusDTO,
});
export const enterState = (stateId: string) =>
  MOCK ? mock.enterState(stateId) : enterStateRequest({ body: { stateId } });

const finishStateRequest = api.createRequest({
  url: url("shift/state/finish"),
  method: METHODS.POST,
  entity: MerchantEmployeeShiftStatusDTO,
});
export const finishState = (body: FinishStateBody) => (MOCK ? mock.finishState() : finishStateRequest({ body }));

const closeShiftRequest = api.createRequest({ url: url("shift/close"), method: METHODS.POST });
// Ответ пустой — новое состояние экран дочитывает сам.
export const closeShift = async () => (MOCK ? mock.closeShift() : void (await closeShiftRequest()));

const statsRequest = api.createRequest({
  url: url("shift/stats"),
  method: METHODS.GET,
  entity: MerchantEmployeeShiftStatsDTO,
});
export const getStats = () => (MOCK ? mock.getStats() : statsRequest());

const documentsRequest = api.createRequest({
  url: url("shift/state/documents"),
  method: METHODS.GET,
  entity: MerchantEmployeeDocumentsResponseDTO,
});
export const getDocuments = async (outcomeId: string): Promise<CandidateDocument[]> =>
  MOCK ? mock.getDocuments() : (await documentsRequest({ additionalQueryParams: { outcomeId } })).documents;
